// src/services/OneDriveService.ts
import { BaseService } from "@services/BaseService";

/**
 * Strongly typed helpers (extend as your API evolves)
 */
export type OneDriveId = string;

export interface UploadFromDrivePayload {
  project_id: OneDriveId;
  file_id: string;
  destination_folder_id?: OneDriveId | null;
  // add any other fields your backend expects
}

export interface PaginatedQuery {
  offset?: number;
  limit?: number;
}

export interface OneDriveSelectedItem {
  id: string;
  name: string;
  mimeType: string; // "application/vnd.onedrive-folder" for folders, real mime for files
}

class OneDriveService extends BaseService {
  /**
   * OAuth helpers
   */
  getAuthUrl() {
    return this.get("/onedrive/auth-url");
  }

  getCallback(params: Record<string, string>) {
    return this.get("/onedrive/callback", { params });
  }

  /**
   * Picker helpers
   */
  getPickerToken() {
    return this.get("/onedrive/picker-token");
  }

  getPickerFiles() {
    return this.get("/onedrive/picker/files");
  }

  getPickerFolderContents(folderId: OneDriveId) {
    return this.get(`/onedrive/picker/folder/${encodeURIComponent(folderId)}`);
  }

  /**
   * Upload from OneDrive -> your storage
   */
  uploadFromDrive(projectId: OneDriveId, items: OneDriveSelectedItem[]) {
    const params: Record<string, string | number> = { project_id: projectId };

    return this.post("/onedrive/upload-from-drive", items, { params });
  }

  /**
   * Sync operations
   */
  syncProject(projectId: OneDriveId) {
    return this.post(`/onedrive/sync/${encodeURIComponent(projectId)}`);
  }

  syncFolder(projectId: OneDriveId, folderId: OneDriveId) {
    return this.post(
      `/onedrive/sync/${encodeURIComponent(
        projectId
      )}/folder/${encodeURIComponent(folderId)}`
    );
  }

  /**
   * Project folders & files
   */
  getProjectFolders(projectId: OneDriveId) {
    return this.get(`/onedrive/${encodeURIComponent(projectId)}/folders`);
  }

  getFolderFiles(projectId: OneDriveId, folderId: OneDriveId) {
    return this.get(
      `/onedrive/${encodeURIComponent(projectId)}/folders/${encodeURIComponent(
        folderId
      )}/files`
    );
  }

  getExternalFiles(projectId: OneDriveId, offset: number, limit: number) {
    return this.get(
      `/onedrive/${encodeURIComponent(projectId)}/external-files`,
      {
        params: { offset, limit },
      }
    );
  }

  deleteFolder(projectId: OneDriveId, folderId: OneDriveId) {
    return this.delete(
      `/onedrive/${encodeURIComponent(projectId)}/folders/${encodeURIComponent(
        folderId
      )}`
    );
  }
}

export const oneDriveService = new OneDriveService();
export default OneDriveService;
